/**
 * v0.23.1 (ADR-123; B-N1, Q3, Q5) — chọn câu cho dải cảnh báo ở đầu web quầy.
 *
 * Hai nguồn, chung một dải và chung khoá "Đã biết" (`sapa.daBietDatLaiMa`, chỉ ẩn trên máy này):
 *   · `datLaiMa`  — mã của chủ vừa được đặt lại qua Zalo / bởi SAPA (ADR-072; hình dạng cũ, bản web cũ đọc `cach`);
 *   · `doiChuGanDay` — việc NGƯỜI TRONG TIỆM làm tới một chủ trong 7 ngày (giao / bỏ vai, cho nghỉ / đi làm lại, đặt lại mã, đổi số),
 *     trừ việc do chính phiên này làm (máy chủ lọc). Trường riêng để bản web cũ bỏ qua thay vì hiện sai chữ.
 * Hiện MỘT mục chưa "Đã biết": Zalo / SAPA trước, rồi việc trong tiệm. Đuôi "Không phải người trong tiệm làm? Gọi SAPA ngay." do dải thêm.
 */
import type { ToiLaAi } from './api.ts'
import { Q } from './text/quen-ma.ts'

export type DoiChuGanDay = NonNullable<ToiLaAi['doiChuGanDay']>[number]
export type DoiQuanLyGanDay = NonNullable<ToiLaAi['doiQuanLyGanDay']>[number]
type DatLaiMa = NonNullable<ToiLaAi['datLaiMa']>[number]

/** Tên người làm: chính người đang xem (`laToi`, B-N4) -> "Bạn"; không có người làm (máy chủ trả rỗng, B2-L1) -> "Ai đó". */
function tenNguoiLam(laToi: boolean, ai: string): string {
  if (laToi) return Q.daiBan
  return ai.trim() === '' ? Q.daiAiDo : ai
}

export function cauDaiDoiChu(x: DoiChuGanDay): string {
  const laToi = x.laToi === true
  const ai = tenNguoiLam(laToi, x.ai)
  switch (x.viec) {
    case 'cap-chu':
      return Q.daiCapChu(ai, x.ten, x.luc)
    case 'bo-chu':
      return Q.daiBoChu(ai, x.ten, x.luc)
    case 'cho-nghi-chu':
      return Q.daiChoNghiChu(ai, x.ten, x.luc)
    case 'di-lam-lai-chu':
      return Q.daiDiLamLaiChu(ai, x.ten, x.luc)
    case 'dat-ma-chu':
      return Q.daiDatMaChu(ai, x.ten, x.luc)
    case 'doi-so-chu':
      // Tự đổi số của mình: "Bạn vừa đổi số đăng nhập lúc …" (cả hai tên cùng thành "Bạn" nên hàm chữ chọn câu ngắn).
      return Q.daiDoiSoChu(ai, laToi && x.ai === x.ten ? Q.daiBan : x.ten, x.luc)
  }
}

/** Việc chạm tới Quản lý / Lễ tân (B-V2). */
export function cauDaiQuanLy(x: DoiQuanLyGanDay): string {
  const ai = tenNguoiLam(x.laToi, x.ai)
  const vai = Q.tenVaiDai[x.vai]
  switch (x.viec) {
    case 'them':
      return Q.daiThemNguoi(ai, vai, x.ten, x.luc)
    case 'giao-vai':
      return Q.daiGiaoVai(ai, vai, x.ten, x.luc)
    case 'dat-ma':
      return Q.daiDatMaNguoi(ai, vai, x.ten, x.luc)
    case 'doi-so':
      return Q.daiDoiSoNguoi(ai, vai, x.ten, x.luc)
    case 'di-lam-lai':
      return Q.daiDiLamLaiNguoi(ai, vai, x.ten, x.luc)
  }
}

/**
 * `nguon` quyết định màu (QV3): Zalo / SAPA (từ ngoài) = đỏ; việc người trong tiệm làm = vàng.
 * `laToi` quyết định đuôi: "Không phải bạn làm? Gọi SAPA ngay." thay cho "Không phải người trong tiệm làm? …".
 */
export function chonMucDai(x: {
  datLaiMa: readonly DatLaiMa[]
  doiChu: readonly DoiChuGanDay[] | undefined
  doiQuanLy?: readonly DoiQuanLyGanDay[] | undefined
  daBiet: readonly string[]
}): { id: string; cau: string; nguon: 'zalo-sapa' | 'trong-tiem'; laToi: boolean } | null {
  const chuaBiet = (m: { id: string }) => !x.daBiet.includes(m.id)
  const zaloSapa = x.datLaiMa.find(chuaBiet)
  if (zaloSapa !== undefined) {
    return {
      id: zaloSapa.id,
      cau:
        zaloSapa.cach === 'zalo'
          ? Q.daiZalo(zaloSapa.ten, zaloSapa.luc)
          : Q.daiSapa(zaloSapa.ten, zaloSapa.luc),
      nguon: 'zalo-sapa',
      laToi: false,
    }
  }
  const chu = (x.doiChu ?? []).find(chuaBiet)
  if (chu !== undefined) {
    return { id: chu.id, cau: cauDaiDoiChu(chu), nguon: 'trong-tiem', laToi: chu.laToi === true }
  }
  const quanLy = (x.doiQuanLy ?? []).find(chuaBiet)
  return quanLy === undefined
    ? null
    : { id: quanLy.id, cau: cauDaiQuanLy(quanLy), nguon: 'trong-tiem', laToi: quanLy.laToi }
}
